import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import {
  Box,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { styles } from './styles';

type LabeledItem = {
  label: string;
  detail?: string;
};

const BulletList = ({ items }: { items: Array<string | LabeledItem> }) => (
  <Box component="ul" sx={styles.bulletList}>
    {items.map((item) => {
      if (typeof item === 'string') {
        return (
          <Box component="li" key={item} sx={styles.bulletItem}>
            <Typography variant="body1" sx={styles.body}>
              {item}
            </Typography>
          </Box>
        );
      }

      return (
        <Box component="li" key={item.label} sx={styles.bulletItem}>
          <Typography variant="body1" sx={styles.body}>
            <Box component="span" sx={styles.bulletLabel}>
              {item.label}
            </Box>
            {item.detail ? ` – ${item.detail}` : null}
          </Typography>
        </Box>
      );
    })}
  </Box>
);

const Section = ({
  title,
  children,
  prominence = 'default',
}: {
  title: string;
  children: ReactNode;
  /** Major sections sit between page title and normal section headings. */
  prominence?: 'major' | 'default';
}) => (
  <Box
    component="section"
    sx={prominence === 'major' ? styles.majorSection : styles.section}
  >
    <Typography
      component={prominence === 'major' ? 'h2' : 'h3'}
      sx={
        prominence === 'major' ? styles.majorSectionTitle : styles.sectionTitle
      }
    >
      {title}
    </Typography>
    <Divider sx={styles.sectionDivider} />
    {children}
  </Box>
);

const Body = ({
  children,
  sx,
}: {
  children: ReactNode;
  sx?: Record<string, unknown>;
}) => (
  <Typography variant="body1" sx={{ ...styles.body, ...sx }}>
    {children}
  </Typography>
);

export const GettingStarted = () => {
  const { t } = useTranslation();

  return (
    <Box sx={styles.page}>
      <Paper elevation={0} sx={styles.paper}>
        <Stack spacing={0}>
          <Typography component="h1" sx={styles.pageTitle}>
            {t('gettingStarted.title')}
          </Typography>

          <Body sx={styles.lead}>{t('gettingStarted.intro1')}</Body>
          <Body sx={{ mb: 1 }}>{t('gettingStarted.intro2')}</Body>

          <Section title={t('gettingStarted.dataset.title')} prominence="major">
            <Body>{t('gettingStarted.dataset.body')}</Body>
          </Section>

          <Section
            title={t('gettingStarted.buildSearch.title')}
            prominence="major"
          >
            <Body>{t('gettingStarted.buildSearch.body')}</Body>
          </Section>

          <Section title={t('gettingStarted.startBroad.title')}>
            <Body sx={{ mb: 1.5 }}>{t('gettingStarted.startBroad.intro')}</Body>
            <Body sx={{ mb: 1.25 }}>
              {t('gettingStarted.startBroad.typicallyInclude')}
            </Body>
            <BulletList
              items={[
                {
                  label: 'Country, Region, State/Province',
                  detail: '(e.g., USA, Canada, Iowa, British Columbia)',
                },
                {
                  label: 'Claim Type',
                  detail:
                    'the general context in which the abuse occurred (e.g., Church, Medical, Sports)',
                },
                {
                  label: 'Organization',
                  detail:
                    'the broader institution (e.g., Roman Catholic Church)',
                },
                {
                  label: 'Subsidiary',
                  detail:
                    'the specific subset entity within the organization (e.g., Diocese of Charlotte, NC)',
                },
                {
                  label: 'Victim Age Category',
                  detail:
                    'age period at time of abuse (e.g., Prepubescent, Teen, Adult)',
                },
              ]}
            />
            <Body sx={{ mt: 2, mb: 1.25 }}>
              {t('gettingStarted.startBroad.showResults')}
            </Body>
            <Body>{t('gettingStarted.startBroad.baseline')}</Body>
          </Section>

          <Section title={t('gettingStarted.defaults.title')}>
            <Body sx={{ mb: 1.5 }}>{t('gettingStarted.defaults.intro')}</Body>
            <Body sx={{ mb: 1.25 }}>
              {t('gettingStarted.defaults.byDefault')}
            </Body>
            <BulletList
              items={[
                t('gettingStarted.defaults.classAction'),
                t('gettingStarted.defaults.bankruptcy'),
                t('gettingStarted.defaults.compensation'),
              ]}
            />
            <Body sx={{ mt: 2 }}>{t('gettingStarted.defaults.note')}</Body>
          </Section>

          <Section title={t('gettingStarted.refine.title')}>
            <Body sx={{ mb: 1.25 }}>{t('gettingStarted.refine.intro')}</Body>
            <BulletList
              items={[
                {
                  label: 'Abuse severity',
                  detail:
                    'There are five levels of abuse in ascending order of severity',
                },
                {
                  label: 'Victim characteristics',
                  detail: '(e.g., Gender, Age Range)',
                },
                {
                  label: 'Perpetrator role',
                  detail: '(e.g., Teacher, Coach, Minister)',
                },
                {
                  label: 'Liability defenses',
                  detail:
                    '(e.g., Statute of Limitations, Charitable Immunity, False Accusation)',
                },
                {
                  label: 'Resolution type',
                  detail:
                    '(e.g., Jury Verdict, Bench Verdict, Settlement, Court Dismissal)',
                },
                {
                  label: 'Resolution date',
                  detail: 'You may choose a date range for claim resolutions',
                },
              ]}
            />
            <Box sx={styles.callout}>
              <Typography sx={styles.calloutText}>
                {t('gettingStarted.refine.important')}
              </Typography>
            </Box>
            <Body sx={{ mt: 2 }}>{t('gettingStarted.refine.closing')}</Body>
          </Section>

          <Section title={t('gettingStarted.compare.title')}>
            <Body sx={{ mb: 1.5 }}>{t('gettingStarted.compare.p1')}</Body>
            <Body sx={{ mb: 1.5 }}>{t('gettingStarted.compare.p2')}</Body>
            <Body>{t('gettingStarted.compare.p3')}</Body>
          </Section>

          <Section title={t('gettingStarted.results.title')}>
            <Body sx={{ mb: 1.5 }}>{t('gettingStarted.results.intro')}</Body>
            <Body sx={{ mb: 1.25 }}>{t('gettingStarted.results.reports')}</Body>
            <BulletList
              items={[
                {
                  label: 'Mean',
                  detail: 'Arithmetic average of all returned claim values.',
                },
                {
                  label: 'Median',
                  detail: 'Midpoint value of all returned claim values.',
                },
                {
                  label: 'Mode',
                  detail:
                    'Most frequently occurring claim value, when applicable.',
                },
                {
                  label: 'Lowest Quartile Mean',
                  detail: 'Average value of the lowest 25% of claims.',
                },
                {
                  label: 'Middle-Half Mean',
                  detail: 'Average value of the middle 50% of claims.',
                },
                {
                  label: 'Highest Quartile Mean',
                  detail: 'Average value of the highest 25% of claims.',
                },
              ]}
            />
            <Body sx={{ mt: 2 }}>{t('gettingStarted.results.closing')}</Body>
          </Section>

          <Section title={t('gettingStarted.sampleSize.title')}>
            <Body sx={{ mb: 1.5 }}>{t('gettingStarted.sampleSize.p1')}</Body>
            <Body sx={{ mb: 1.5 }}>{t('gettingStarted.sampleSize.p2')}</Body>
            <Body>{t('gettingStarted.sampleSize.p3')}</Body>
          </Section>

          <Section title={t('gettingStarted.save.title')}>
            <Body sx={{ mb: 1.5 }}>{t('gettingStarted.save.intro')}</Body>
            <Body sx={{ mb: 1.25 }}>{t('gettingStarted.save.valuable')}</Body>
            <BulletList
              items={[
                t('gettingStarted.save.comparing'),
                t('gettingStarted.save.monitoring'),
                t('gettingStarted.save.returning'),
                t('gettingStarted.save.sharing'),
              ]}
            />
          </Section>

          <Section title={t('gettingStarted.bestPractices.title')}>
            <List disablePadding sx={styles.practicesList}>
              {[
                t('gettingStarted.bestPractices.beginBroad'),
                t('gettingStarted.bestPractices.oneFilter'),
                t('gettingStarted.bestPractices.showResults'),
                t('gettingStarted.bestPractices.compare'),
                t('gettingStarted.bestPractices.save'),
              ].map((item) => (
                <ListItem key={item} disableGutters sx={styles.practiceItem}>
                  <ListItemIcon sx={styles.practiceIcon}>
                    <CheckCircleOutlineIcon color="primary" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={item}
                    primaryTypographyProps={{ sx: styles.body }}
                  />
                </ListItem>
              ))}
            </List>
            <Box sx={styles.note}>
              <Typography sx={styles.noteText}>
                {t('gettingStarted.bestPractices.saveNote')}
              </Typography>
            </Box>
          </Section>

          <Box sx={styles.closing}>
            <Typography sx={styles.body}>
              {t('gettingStarted.closing')}
            </Typography>
          </Box>
        </Stack>
      </Paper>
    </Box>
  );
};
